// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {AfterViewInit, ViewChild, Component, DestroyRef, inject, QueryList, Input, OnInit, ViewEncapsulation, ViewChildren} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    CdkDragDrop,
    CdkDropList,
    moveItemInArray,
    transferArrayItem,
    CdkDragRelease,
    CdkDragMove,
    CdkDrag, CdkDragHandle
} from '@angular/cdk/drag-drop';
import {FormsModule} from '@angular/forms';
import {forkJoin, Observable} from "rxjs";
import {
    FolderEntry,
    isFolderEntry,
    MenuEntry,
    MenuNodeType,
    MenuTreeService,
    DraggableTreeNodeEntry
} from "../../services/menu-tree.service";
import {AppScope} from "../../services/app_scope.service";
import {ApiService} from "../../services/api/api.service";
import {HeaderDataService} from "../../services/header_data.service";
import {defaultIfEmpty, finalize, take} from "rxjs/operators";
import {IDMap, ModelID} from "../../_typing/generic-types";
import {FolderSessionState, FolderSessionStatePatch} from "../../_models/folder-session-state";
import {Folder, FolderPatch} from "../../_models/folder";
import {SessionStatePatch} from "../../_models/session-state";
import {NotificationService} from "../../services/notification.service";
import {HttpErrorHandler} from "../../services/http-error-handler.service";
import {DragdropTreeService} from "./dragdrop-tree.service";
import {Router, RouterLink} from "@angular/router";
import {CommonModule} from "@angular/common";
import {FormDialogService} from "../../services/form-dialog.service";
import {UserService} from "../../services/user.service";
import {AccountService} from "../../services/account.service";
import {validateEntityName} from "../../shared/entity-name-validator";
import {
    EntityNameEditorComponent,
    RenameRequest
} from "../../components/entity-name-editor/entity-name-editor.component";


@Component({
    selector: 'dragdrop-tree',
    templateUrl: './dragdrop-tree.component.html',
    styleUrls: ['./dragdrop-tree.component.scss'],
    imports: [
        CommonModule,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        FormsModule,
        RouterLink,
        EntityNameEditorComponent,
    ],
    encapsulation: ViewEncapsulation.None
})
export class DragdropTreeComponent implements OnInit, AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);

    // The component renders folder structure: every recursive
    // `<dragdrop-tree>` is gated by `*ngIf="child.type === MenuNodeType.FOLDER"`,
    // and the template touches folder-only fields like `total_page_count`.
    // Narrowing the input type matches that contract.
    @Input() node: FolderEntry;
    @Input() expandedNodes: IDMap<boolean> = {};

    protected readonly MenuNodeType = MenuNodeType;

    isDragging: boolean = false;
    editMode: boolean = false;

    hoveredFolderId: string | null = null;
    hoveredPageId: string | null = null;
    // Id of the row whose inline editor is open. One slot, because only one row can be edited
    // at a time. The draft text lives in the editor, not here.
    editingEntityId: string | null = null;
    // Row ids currently being renamed. Set (not a single slot) so two
    // overlapping renames each guard only their own request — a shared slot let
    // one rename's completion clear the other's guard, and let a later save
    // overwrite an in-flight one and re-arm it for a duplicate PATCH.
    savingRenameIds = new Set<string>();
    // Folder ids currently being duplicated or deleted. Guards against a
    // rage-click / re-open-menu chain firing the same backend request twice.
    private inFlightFolderIds = new Set<string>();

    @ViewChild(CdkDropList) dropList?: CdkDropList;

    allowDropPredicate = (drag: CdkDrag, drop: CdkDropList) => {
        return this.isDropAllowed(drag, drop);
    };

    constructor(
        public appScope: AppScope,
        private api: ApiService,
        public headerData: HeaderDataService,
        private notification: NotificationService,
        public dragDropService: DragdropTreeService,
        private formDialogService: FormDialogService,
        public userService: UserService,
        private menuTreeService: MenuTreeService,
        private httpErrorHandler: HttpErrorHandler,
        private accountService: AccountService,
        private router: Router) {
    }

    ngOnInit() {
        this.headerData.folder_edit_toggle.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.editMode = value;
            });
    }

    ngAfterViewInit() {
        if (this.dropList) {
            this.dragDropService.register(this.dropList);
        }
    }

    isDropAllowed(drag: CdkDrag, drop: CdkDropList) {
        if ((this.dragDropService.currentHoverDropListId === 'top' || drop.id==='top') && drag.data?.type === MenuNodeType.MENU) return false;

        // Folder-into-self or folder-into-own-descendant would persist a
        // parent cycle. FolderDetail.before_patch also rejects this
        // server-side, but blocking it in the predicate stops the drag UI
        // previewing a move the backend would 400 on.
        if (drag.data?.type === MenuNodeType.FOLDER) {
            const draggedEntry = drag.data as FolderEntry;
            if (drop.id === draggedEntry.id) return false;
            if (this.isFolderDescendant(draggedEntry, drop.id)) return false;
        }

        if (this.dragDropService.currentHoverDropListId == null) {
            return true;
        }

        return drop.id === this.dragDropService.currentHoverDropListId;
    }

    private isFolderDescendant(root: FolderEntry, targetId: string): boolean {
        for (const child of root.contents ?? []) {
            if (!isFolderEntry(child)) continue;
            if (child.id === targetId) return true;
            if (this.isFolderDescendant(child, targetId)) return true;
        }
        return false;
    }

    dragMoved(event: CdkDragMove<DraggableTreeNodeEntry>) {
        this.dragDropService.dragMoved(event);
    }

    dragReleased(event: CdkDragRelease) {
        this.dragDropService.dragReleased(event);
    }

    onNodeToggled(node) {
        if (this.isDragging) return;
        this.expandedNodes[node.id] = !this.expandedNodes[node.id];
    }

    onNodeHover(node) {
        if (!this.isDragging) return;
        if (!this.expandedNodes[node.id]) {
            setTimeout(() => {
                this.expandedNodes[node.id] = true;
            }, 1000); // Delay expansion for smoother UI
        }
    }

    drop(event: CdkDragDrop<DraggableTreeNodeEntry[]>) {
        const draggedNode = event.item.data;

        if (event.container.id === 'top' && draggedNode.type === MenuNodeType.MENU) return false;

        // Folders and pages share a single interleaved `order` sequence (the tree
        // sorts both types together), but the drop list is enabled when the user
        // can reorder folders OR pages and we only PATCH the permitted type below.
        // If the user can reorder just one type, a move that shifts the OTHER
        // type's rows would persist an inconsistent order (the un-PATCHed rows keep
        // order values that do not fit the new sequence). Snapshot the
        // un-permitted rows' positions (by object identity — the move reorders the
        // same references) so we can detect and refuse such a move.
        const canReorderFolders = this.userService.canReorderFolders();
        const canReorderPages = this.userService.canReorderPages();
        const restrictedReorder = !(canReorderFolders && canReorderPages);
        const unpermittedType = canReorderFolders ? MenuNodeType.MENU : MenuNodeType.FOLDER;
        const beforePrev = restrictedReorder
            ? this._positionsByType(event.previousContainer.data, unpermittedType) : null;
        const beforeCur = restrictedReorder
            ? this._positionsByType(event.container.data, unpermittedType) : null;

        if (event.previousContainer === event.container) {
            moveItemInArray(
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        } else {
            const targetNode = event.container.data[event.currentIndex];
            if (targetNode && targetNode.type === MenuNodeType.STATIC_FOLDER) {
                this.isDragging = false;
                return;
            }

            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }

        if (restrictedReorder && this._reorderDisturbedOtherType(event, unpermittedType, beforePrev, beforeCur)) {
            // The move shifted a row type the user can't reorder. Revert the
            // optimistic array mutation and tell them, rather than persist a
            // partial order and report a false "Menu saved".
            if (event.previousContainer === event.container) {
                moveItemInArray(event.container.data, event.currentIndex, event.previousIndex);
            } else {
                transferArrayItem(event.container.data, event.previousContainer.data, event.currentIndex, event.previousIndex);
            }
            this.notification.openError(
                canReorderPages
                    ? "You can only reorder pages here, not folders."
                    : "You can only reorder folders here, not pages."
            );
            this.isDragging = false;
            return;
        }

        const $obs: Observable<FolderSessionState | Folder>[] = [];
        for (const [index, item] of event.container.data.entries()) {
            if (isFolderEntry(item)) {
                if (canReorderFolders) {
                    $obs.push(this.updateFolder(event.container, event.previousContainer, item, index));
                }
            } else if (canReorderPages) {
                $obs.push(this.updateFolderSessionState(event.container, event.previousContainer, item, index));
            }
        }

        forkJoin($obs).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => this.notification.openSuccess("Menu saved."),
            error: (err) => {
                console.error("ERROR: DragDropTree (drop) ", err);
                this.notification.openError("There was a problem and the menu order was not saved. Please check the console for details.");
            },
        });

        this.isDragging = false; // Reset after drop
    }

    /** Map each row of `type` in `data` to its index, keyed by object identity. */
    private _positionsByType(
        data: DraggableTreeNodeEntry[],
        type: MenuNodeType
    ): Map<DraggableTreeNodeEntry, number> {
        const positions = new Map<DraggableTreeNodeEntry, number>();
        data.forEach((item, index) => {
            if (item.type === type) positions.set(item, index);
        });
        return positions;
    }

    /** True if any row of `type` changed index (or left/entered) in either
     *  affected container after the move — i.e. the move disturbed a row type the
     *  user is not permitted to reorder. */
    private _reorderDisturbedOtherType(
        event: CdkDragDrop<DraggableTreeNodeEntry[]>,
        type: MenuNodeType,
        beforePrev: Map<DraggableTreeNodeEntry, number>,
        beforeCur: Map<DraggableTreeNodeEntry, number>
    ): boolean {
        const afterPrev = this._positionsByType(event.previousContainer.data, type);
        const afterCur = this._positionsByType(event.container.data, type);
        return this._positionsChanged(beforePrev, afterPrev) ||
            this._positionsChanged(beforeCur, afterCur);
    }

    private _positionsChanged(
        before: Map<DraggableTreeNodeEntry, number>,
        after: Map<DraggableTreeNodeEntry, number>
    ): boolean {
        if (before.size !== after.size) return true;
        for (const [item, index] of before) {
            if (after.get(item) !== index) return true;
        }
        return false;
    }


    updateFolderSessionState(container, previousContainer, item: MenuEntry, index: number): Observable<FolderSessionState> {
        const sameContainer = previousContainer.id === container.id;
        const fssPatch: FolderSessionStatePatch = {
            id: item.folder_session_state_id,
            type: 'folder_session_state',
            attributes: {order: index},
        };
        if (!sameContainer) {
            fssPatch.relationships = {
                folder: {data: {id: container.id, type: 'folder'}},
            };
        }
        return this.api.folder_session_state.obsPatch(fssPatch);
    }

    updateFolder(container, previousContainer, item: FolderEntry, index: number): Observable<Folder> {
        const sameContainer = previousContainer.id === container.id;
        const folderPatch: FolderPatch = {
            id: item.id,
            type: 'folder',
            attributes: {order: index},
        };
        if (!sameContainer) {
            folderPatch.relationships = {
                parent: {
                    data: container.id === 'top' ? null : {id: container.id, type: 'folder'},
                },
            };
        }
        return this.api.folder.obsPatch(folderPatch);
    }

    hasVisibleContent(): boolean {
        if (!isFolderEntry(this.node)) return false;
        if (!this.node.contents?.length) return false;
        return this.node.contents.some(child => {
            if (child.type === MenuNodeType.FOLDER) return true;
            if (child.type === MenuNodeType.MENU) return (child as MenuEntry).visible;
            return false;
        });
    }

    onFolderNameClick(event: MouseEvent, child: FolderEntry) {
        // Gate inline rename on the same permission the context-menu Rename uses
        // (`edit folder`). The broader canManageFoldersAndPages() also passes
        // for page-management roles, which would then 403 on PATCH /folder.
        if (this.editMode && this.userService.canRenameFolder()) {
            event.stopPropagation();
            this.startRename(child);
        }
    }

    onDragStarted(event) {
        this.isDragging = true;
    }

    closeSidenav() {
        if (!this.isDragging) { // Only close if not dragging
            this.appScope.sidenavOpenSubject.next(false);
            this.appScope.sidenav_open = false;
        }
    }

    openContextMenu(event: MouseEvent, child: FolderEntry) {
        event.stopPropagation();
        // Use the unfiltered subtree count: menu_entry_count reflects pages the
        // current user can see (post restricted_access + visibility filters),
        // but the backend's per-action gating walks the real subtree. A folder
        // that looks empty here can have hidden pages, so we must take the
        // has-pages path to match what the server will enforce.
        const hasPages = child.total_page_count > 0;
        this.formDialogService.openFolderContextMenu(event, {
            inputs: {
                canCreatePage: this.userService.canCreatePage(),
                canCreateFolder: this.userService.canCreateFolder(),
                canRename: this.userService.canRenameFolder(),
                canDelete: this.userService.canDeleteFolder(hasPages),
                canDuplicate: this.userService.canDuplicateFolder(hasPages),
            },
            onCreatePage: () => this.openNewPageInFolder(child.id),
            onCreateFolder: () => this.formDialogService.openFolderDialog(child.id),
            onRename: () => this.startRename(child),
            onDuplicate: () => this.duplicateFolder(child),
            onDelete: () => this.deleteFolder(child),
        });
    }

    /**
     * The page row's `⋯` menu. Rename is the only action for now — the folder menu component is
     * reused in 'page' mode rather than adding a second popup, and it renders only the items it
     * is handed a callback for.
     */
    openPageContextMenu(event: MouseEvent, entry: MenuEntry) {
        event.stopPropagation();
        this.formDialogService.openFolderContextMenu(event, {
            inputs: {
                mode: 'page',
                canRename: this.userService.canRenamePage(),
            },
            onRename: () => this.startRename(entry),
        });
    }

    // Open the new-page dialog pre-filled with this folder, file the page there, and leave the
    // user exactly where they are.
    //
    // This deliberately REVERSES F47 (MR !7160 round-2), which added a `navigateByUrl` here so the
    // user would "land on the new page". At the time that also switched edit mode OFF, because
    // navigating destroys the page-view and `ngOnDestroy` reset `folder_edit_toggle` — so someone
    // organising folders could create ONE page and then had to re-enable edit mode before they
    // could do anything else (Qase C480). That half is now fixed at the source: the sidebar owns
    // its edit mode and navigation does not end it. The reversal still stands on its own, because
    // navigating also loaded a page the user may never have wanted.
    //
    // Refreshing the tree is what makes the new page visible, and that is enough on its own: the
    // page appears under its folder, and the dialog has already raised its own "Page saved" toast.
    // `page-view.openNewPageForm()` still navigates — asking for a new page from the page itself is
    // a different intent from filing one while tidying folders.
    async openNewPageInFolder(folderId: string) {
        const dialogRef = await this.formDialogService.openNewPageDialog(folderId);
        dialogRef.afterClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(result => {
            if (result) {
                this.menuTreeService.refresh();
            }
        });
    }

    /**
     * The per-entity half of a rename: how to read and write the displayed name, how to PATCH it,
     * and what to call it in the toasts. Everything else — validation, the optimistic update, the
     * in-flight guard, the error surfacing — is identical for folders and pages and stays in
     * saveRename().
     *
     * `currentName` is captured when this is built, i.e. at save time, so it is the committed
     * name and not a value that went stale while an earlier PATCH failed.
     */
    private renameTarget(entry: DraggableTreeNodeEntry) {
        if (isFolderEntry(entry)) {
            const folder = entry.folder;
            return {
                id: entry.id,
                label: 'Folder',
                currentName: folder.attributes.name,
                setName: (name: string) => { folder.attributes.name = name; },
                patch: (name: string) => this.api.folder.obsPatch(
                    {id: entry.id, type: 'folder', attributes: {name}} as FolderPatch, true),
                successMessage: 'Folder renamed',
                errorPrefix: 'Failed to rename folder: ',
            };
        }
        // MenuEntry carries no id of its own — the page's identity is its session_state.
        const page = (entry as MenuEntry).session_state;
        return {
            id: page.id,
            label: 'Page',
            currentName: page.attributes.name,
            setName: (name: string) => { page.attributes.name = name; },
            patch: (name: string) => this.api.session_state.obsPatch(
                {id: page.id, type: 'session_state', attributes: {name}} as SessionStatePatch, true),
            successMessage: 'Page renamed',
            errorPrefix: 'Failed to rename page: ',
        };
    }

    startRename(entry: DraggableTreeNodeEntry) {
        this.editingEntityId = this.renameTarget(entry).id;
    }

    cancelRename() {
        this.editingEntityId = null;
    }

    saveRename(entry: DraggableTreeNodeEntry, request: RenameRequest) {
        const target = this.renameTarget(entry);

        if (this.savingRenameIds.has(target.id)) {
            // A PATCH for this row is still in flight. Refuse the second one rather than racing
            // it — two renames of one row would land in arbitrary order — but close the editor
            // so the user is not left typing into a box that will never save (F87's intent).
            this.editingEntityId = null;
            return;
        }

        const result = validateEntityName(request.name, target.label);
        if (!result.valid) {
            this.notification.openError(result.error);
            // On blur the user has signalled they're done — close the editor. On an explicit
            // save (Enter / ✓) keep it open so they can fix the name.
            if (request.source === 'blur') {
                this.cancelRename();
            }
            return;
        }

        const newName = request.name.trim();
        this.editingEntityId = null;

        // Read the committed name at save time. The old code captured this when the editor
        // OPENED, which went stale if a first PATCH failed and reverted the displayed name.
        const previousName = target.currentName;
        if (newName === previousName) {
            return;
        }

        // Optimistically update the displayed name immediately.
        target.setName(newName);
        this.savingRenameIds.add(target.id);

        // `silent`: raise the notification here instead, so the server's reason
        // (e.g. "You do not have permission to edit a page in this account")
        // reaches the user. The shared handler's toast would otherwise be
        // dismissed by ours a tick later.
        target.patch(newName).pipe(
            take(1),
            finalize(() => this.savingRenameIds.delete(target.id)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: () => {
                this.menuTreeService.refresh();
                this.notification.openSuccess(target.successMessage);
            },
            error: (e) => {
                console.error('ERROR: DragDropTree (saveRename) ', e);
                target.setName(previousName);
                this.httpErrorHandler.showError(e, target.errorPrefix);
            }
        });
    }

    // Adapters put their message in `error.error` as a plain string; route-level
    // failures nest it under `error.error.message`. httpErrorHandler.showError()
    // reads neither shape, so folder actions unwrap it here.
    private folderActionError(e: any, fallback: string): string {
        const detail = e?.error?.error;
        return typeof detail === 'string' ? detail : (detail?.message ?? fallback);
    }

    duplicateFolder(child: FolderEntry) {
        if (this.inFlightFolderIds.has(child.id)) return;
        this.inFlightFolderIds.add(child.id);
        this.notification.openInfo('Duplicating folder...');
        this.menuTreeService.copyFolder(child.id).pipe(
            take(1),
            finalize(() => this.inFlightFolderIds.delete(child.id)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: () => {
                this.menuTreeService.refresh();
                this.notification.openSuccess('Folder duplicated');
            },
            error: (e) => {
                console.error('ERROR: DragDropTree (duplicateFolder) ', e);
                this.notification.openError(this.folderActionError(e, 'Failed to duplicate folder'));
            }
        });
    }

    async deleteFolder(child: FolderEntry) {
        if (this.inFlightFolderIds.has(child.id)) return;
        const confirmed = await this.formDialogService.confirm(
            'This will permanently delete this folder, all nested sub-folders, and any pages that are only linked here. Pages shared with other folders will be kept. This cannot be undone.',
            'Cancel', 'Delete'
        );
        if (!confirmed) return;
        // Re-check after the await — a second click could have started during the dialog.
        if (this.inFlightFolderIds.has(child.id)) return;
        this.inFlightFolderIds.add(child.id);

        this.notification.openInfo('Deleting folder...');
        this.menuTreeService.deleteFolder(child.id).pipe(
            take(1),
            finalize(() => this.inFlightFolderIds.delete(child.id)),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: (response) => {
                this.menuTreeService.refresh();
                this.notification.openSuccess('Folder deleted');
                // If the page currently open was deleted (not merely unlinked),
                // the route still points at a now-missing page — send the user
                // to their default page.
                this.navigateAwayIfCurrentPageDeleted(response?.deleted_page_ids ?? []);
            },
            error: (e) => {
                console.error("ERROR: DragDropTree (deleteFolder) ", e);
                this.notification.openError(this.folderActionError(e, 'Failed to delete folder'));
            }
        });
    }

    /** The open page's id parsed from the current `/view/page_view/<id>` URL, or null. */
    private currentPageId(): string | null {
        const match = this.router.url.match(/\/view\/page_view\/([^/?#]+)/);
        return match ? match[1] : null;
    }

    private navigateAwayIfCurrentPageDeleted(deletedPageIds: ModelID[]) {
        const currentId = this.currentPageId();
        const deletedIds = new Set(deletedPageIds.map(id => String(id)));
        // Early return BEFORE the lookup below: resolving the default costs two
        // requests, and the overwhelmingly common delete (the open page survives,
        // or nothing was deleted at all) must stay request-free.
        if (!currentId || !deletedIds.has(currentId)) return;
        // Deliberately NOT `current_user.default_dashboard_id`, and deliberately
        // NOT mirroring url-guard: that value is resolved once at login for
        // whichever account was active then, and `AccountService.setActiveAccount`
        // never refreshes it. Using it here would route a user who switched from
        // account A to B into A's default page, and `pageViewGuard` would then
        // flip the active account back to A behind them. Resolve the CURRENT
        // account's default instead — the same helper the account picker uses,
        // which also honours the user's per-account UserPreferences default.
        const accountId = this.appScope.active_account_id;
        if (!accountId) {
            // getAccountFilter() substitutes the internal WIREBaseConfig account for a
            // missing id rather than failing, so asking without one queries the wrong
            // account entirely. Nothing useful can come back — go straight to the list.
            this.router.navigate(['view', 'page_list_view']);
            return;
        }
        this.accountService.getDefaultUserSessionState(
            accountId, this.appScope.current_user?.id
        ).pipe(
            take(1),
            // An observable that COMPLETES WITHOUT EMITTING fires neither `next`
            // nor `error`, which would strand the user on the page they just
            // deleted. getDefaultUserSessionState is a combineLatest over two
            // searches, so an empty completion is reachable; collapse it to the
            // page-list fallback rather than to silence.
            defaultIfEmpty(null),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: (defaultId: string) => {
                // The `deletedIds` check is near-unreachable rather than dead: the
                // account default page cannot be destroyed by a folder delete (the
                // adapter's is_default guard, 409), and a UserPreferences default
                // that WAS destroyed has already had its FK nulled server-side
                // before this response. Kept as a cheap backstop against routing
                // the user straight back onto a missing page.
                if (defaultId && !deletedIds.has(String(defaultId))) {
                    this.router.navigate(['view', 'page_view', defaultId]);
                } else {
                    this.router.navigate(['view', 'page_list_view']);
                }
            },
            // The delete itself already succeeded; failing to resolve a default
            // must still move the user off the now-missing page.
            error: () => this.router.navigate(['view', 'page_list_view'])
        });
    }
}
